import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Smartphone, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const ROLE_MAP = {
  business: { label: "Comercio", loginPath: "/login" },
  customer: { label: "Cliente", loginPath: "/login-cliente" },
  promoter: { label: "Promotor", loginPath: "/login-promotor" },
};

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const defaultRole = ROLE_MAP[params.get("rol")] ? params.get("rol") : "business";

  const [role, setRole] = useState(defaultRole);
  const [whatsapp, setWhatsapp] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState(1); // 1 = request OTP, 2 = enter OTP + new pass, 3 = done
  const [busy, setBusy] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { whatsapp, role });
      setStep(2);
      toast.success("Si tu número está registrado, recibirás un código por WhatsApp.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar código");
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { whatsapp, role, otp, new_password: newPassword });
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código incorrecto o expirado");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="overline text-[hsl(var(--primary))]">Recuperar acceso</div>
        <h1 className="font-display text-4xl tracking-tight mt-2">¿Olvidaste tu contraseña?</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Te enviamos un código de 6 dígitos por WhatsApp.
        </p>

        {step === 1 && (
          <form onSubmit={requestOtp} className="mt-8 bg-white border border-border rounded-2xl p-6 space-y-5" data-testid="forgot-password-form">
            <div>
              <Label className="text-sm">Tipo de cuenta</Label>
              <div className="mt-1.5 flex gap-2">
                {Object.entries(ROLE_MAP).map(([key, { label }]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setRole(key)}
                    className={`flex-1 py-2 rounded-full text-sm font-medium border transition-colors ${
                      role === key
                        ? "bg-[#E05A47] text-white border-[#E05A47]"
                        : "border-border text-zinc-600 hover:border-zinc-400"
                    }`}
                    data-testid={`forgot-role-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm">Celular registrado (10 dígitos)</Label>
              <div className="mt-1.5 relative">
                <Smartphone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  required
                  inputMode="numeric"
                  className="pl-9"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value.replace(/\D/g, "").slice(0, 12))}
                  placeholder="5512345678"
                  data-testid="forgot-whatsapp-input"
                />
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full btn-primary rounded-full py-6" data-testid="forgot-send-otp-btn">
              {busy ? "Enviando…" : "Enviar código por WhatsApp"}
            </Button>
            <div className="text-sm text-center text-muted-foreground">
              <Link to={ROLE_MAP[role].loginPath} className="text-[hsl(var(--primary))] font-medium hover:underline">
                Volver al inicio de sesión
              </Link>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={resetPassword} className="mt-8 bg-white border border-border rounded-2xl p-6 space-y-5" data-testid="reset-password-form">
            <div className="text-sm text-zinc-600 bg-zinc-50 rounded-xl p-3 border border-border">
              Código enviado al <span className="font-semibold">{whatsapp}</span>. Válido 10 minutos.
            </div>
            <div>
              <Label className="text-sm">Código de 6 dígitos</Label>
              <Input
                required
                inputMode="numeric"
                maxLength={6}
                className="mt-1.5 tracking-widest text-center text-lg font-mono"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                data-testid="reset-otp-input"
              />
            </div>
            <div>
              <Label className="text-sm">Nueva contraseña</Label>
              <Input
                required
                type="password"
                minLength={4}
                className="mt-1.5"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 4 caracteres"
                data-testid="reset-new-password-input"
              />
            </div>
            <Button type="submit" disabled={busy || otp.length < 6 || newPassword.length < 4} className="w-full btn-primary rounded-full py-6" data-testid="reset-password-submit-btn">
              {busy ? "Guardando…" : "Cambiar contraseña"}
            </Button>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full text-sm text-muted-foreground hover:underline"
            >
              ¿No recibiste el código? Volver a enviar
            </button>
          </form>
        )}

        {step === 3 && (
          <div className="mt-8 bg-white border border-border rounded-2xl p-8 text-center space-y-4" data-testid="reset-success">
            <CheckCircle2 className="w-12 h-12 text-[#2E5B3D] mx-auto" />
            <h2 className="font-display text-2xl">¡Contraseña actualizada!</h2>
            <p className="text-sm text-muted-foreground">Ya puedes iniciar sesión con tu nueva contraseña.</p>
            <Link to={ROLE_MAP[role].loginPath}>
              <Button className="btn-primary rounded-full px-8 py-5 mt-2" data-testid="reset-go-login-btn">
                <KeyRound className="w-4 h-4 mr-2" />
                Ir a iniciar sesión
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
